// core/services/casesService.ts — B5: API-backed moderation state machine.
//
// The state machine (votes, 4-of-7 auto-resolution, appeals) now runs server-side
// (apps/moderation). This service is a thin apiClient wrapper that hydrates the
// existing Zustand caseStore so the live moderation banners keep working.
// Signatures + _resetForTest are unchanged.

import type { CaseRecord, ModerationCategory, VoteSide } from '@/core/models/case';
import { useCaseStore } from '@/core/stores/caseStore';
import { seedCases } from '@/core/data/cases';
import { api } from '@/core/lib/apiClient';

export const THRESHOLD = 4;

function getCases(): CaseRecord[] {
  return useCaseStore.getState().cases;
}

export interface CreatePostCaseInput {
  kind: 'post';
  targetId: string;
  category: ModerationCategory;
  context?: string;
}

export interface CreateAccountCaseInput {
  targetId: string;
  category: ModerationCategory;
  context?: string;
}

function upsert(c: CaseRecord): void {
  const store = useCaseStore.getState();
  if (store.cases.some((x) => x.id === c.id)) store.patchCase(c.id, c);
  else store.addCase(c);
}

export const casesService = {
  /** All cases, most-recent first (server already reverse-chron). Hydrates the store.
   *
   * The shape is CHECKED before it reaches the store. `cases` is typed as an
   * array and every reader spreads it (`[...s.cases]` in useCases), so storing
   * anything else turns the next render into a TypeError — and the public
   * record page renders straight off this store for a signed-out visitor, who
   * would get a blank page instead of an empty record. An error envelope, a
   * proxied HTML error page, or an older server answering a different shape all
   * land here; none of them should be able to take a page down. */
  async listCases(): Promise<CaseRecord[]> {
    const body = await api<CaseRecord[]>('/cases/');
    const list = Array.isArray(body) ? body : [];
    useCaseStore.getState().setCases(list);
    return list;
  },

  async getCase(id: string): Promise<CaseRecord | null> {
    try {
      return await api<CaseRecord>(`/cases/${id}/`);
    } catch {
      return null;
    }
  },

  /** Most-recent case for a post — derived from the hydrated store. */
  async getActiveCaseForPost(postId: string): Promise<CaseRecord | null> {
    const cases = getCases();
    for (let i = cases.length - 1; i >= 0; i--) {
      const c = cases[i]!;
      if (c.kind === 'post' && c.postId === postId) return c;
    }
    return null;
  },

  /** Most-recent case for an account — derived from the hydrated store. */
  async getActiveCaseForAccount(accountId: string): Promise<CaseRecord | null> {
    const cases = getCases();
    for (let i = cases.length - 1; i >= 0; i--) {
      const c = cases[i]!;
      if (c.kind === 'account' && c.accountId === accountId) return c;
    }
    return null;
  },

  async createCaseFromReport(input: CreatePostCaseInput): Promise<CaseRecord> {
    const c = await api<CaseRecord>('/cases/', {
      method: 'POST',
      body: { kind: 'post', targetId: input.targetId, category: input.category, context: input.context ?? '' },
    });
    upsert(c);
    return c;
  },

  async createAccountCaseFromReport(input: CreateAccountCaseInput): Promise<CaseRecord> {
    const c = await api<CaseRecord>('/cases/', {
      method: 'POST',
      body: { kind: 'account', targetId: input.targetId, category: input.category, context: input.context ?? '' },
    });
    upsert(c);
    return c;
  },

  async castVote(caseId: string, side: VoteSide): Promise<CaseRecord | null> {
    const c = await api<CaseRecord>(`/cases/${caseId}/vote/`, { method: 'POST', body: { side } });
    useCaseStore.getState().patchCase(caseId, c);
    return c;
  },

  /**
   * Appeal a decided case. `reason` is REQUIRED — the server rejects a blank one
   * with 400 {code:'validation', fields:{reason:'required'}}. It is not a
   * formality: README section 6 promises the member says why they think the
   * decision was wrong, and that text is the first thing the fresh team reads.
   *
   * Throws ApiError, which callers are expected to discriminate on `.code`:
   *   not_your_case (403) · validation (400) · no_fresh_team (409)
   */
  async appeal(caseId: string, reason: string): Promise<CaseRecord | null> {
    const c = await api<CaseRecord>(`/cases/${caseId}/appeal/`, {
      method: 'POST',
      body: { reason: reason.trim() },
    });
    useCaseStore.getState().patchCase(caseId, c);
    return c;
  },

  /** Reset the store back to the seed (test isolation). */
  _resetForTest(): void {
    useCaseStore.getState().setCases(
      seedCases.map((c) => ({ ...c, votes: { ...c.votes } })),
    );
  },
} as const;
